
import React, { useState, useEffect, useMemo } from 'react';
import EvaluationModal from '../components/EvaluationModal';
import AddLecturerModal from '../components/AddLecturerModal';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || '/.netlify/functions/api';
const DIRECTORY_URL = `${API_BASE}/directory`;
const DIRECTORY_SEARCH_URL = `${API_BASE}/directory/search`;
const DOCTORS_RATINGS_URL = `${API_BASE}/doctors`;

const itemsPerPage = 8;




const stripArabicMarks = (s = '') =>
  s.replace(/[\u0617-\u061A\u064B-\u0652\u0670]/g, '').replace(/\u0640/g, '');
const arVariants = (s = '') =>
  s
    .replace(/[إأآا]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/ة/g, 'ه');
const arNorm = (s = '') =>
  arVariants(stripArabicMarks(String(s).trim().toLowerCase()))
    .replace(/[^0-9a-z\u0600-\u06FF\s-]/gi, '')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ');
const keyNorm = (s = '') => arNorm(s).replace(/\s+/g, '');


const djb2 = (str) => {
  let h = 5381 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  }
  return h >>> 0;
};


const shortCodeFromName = (name = '') => {
  const k = keyNorm(name || '');
  const u32 = djb2(k);
  const b36 = u32.toString(36).toLowerCase();
  return (b36 + '00000000').slice(0, 8);
};


const LecturerEvaluationPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const openHeaderLoginModal = () => {
    try {
      window.dispatchEvent(
        new CustomEvent('open-auth-modal', { detail: { mode: 'login' } })
      );
    } catch {}
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  const [selectedNameFilter, setSelectedNameFilter] = useState('');

  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);

  const [ratingsMap, setRatingsMap] = useState({});
  const [viewsMap, setViewsMap] = useState({});

  const [selectedCollege, setSelectedCollege] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedRatingFilter, setSelectedRatingFilter] = useState('التقييم');
  const [showRatingDropdown, setShowRatingDropdown] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [showAddLecturerModal, setShowAddLecturerModal] = useState(false);

  const fetchJSON = async (url) => {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    const text = await res.text();
    if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
    try {
      return JSON.parse(text);
    } catch {
      throw new Error('Invalid JSON response');
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);

        const base = await fetchJSON(DIRECTORY_URL);
        const baseList = Array.isArray(base) ? base : [];
        if (!mounted) return;
        setDirectory(baseList);

        let ratingsRows = [];
        try {
          const rat = await fetchJSON(DOCTORS_RATINGS_URL);
          ratingsRows = Array.isArray(rat) ? rat : [];
        } catch {
          ratingsRows = [];
        }

        const buildTopTags = (row) => {
          const pools = []
            .concat(Array.isArray(row.test_tags) ? row.test_tags : [])
            .concat(Array.isArray(row.attendance_tags) ? row.attendance_tags : [])
            .concat(Array.isArray(row.explanation_tags) ? row.explanation_tags : [])
            .concat(Array.isArray(row.grade_tags) ? row.grade_tags : []);
          const freq = {};
          for (const t of pools) {
            const label =
              t && (t.label || t.Label || t.tag || t.Tag)
                ? t.label || t.Label || t.tag || t.Tag
                : null;
            if (!label) continue;
            const key = String(label).trim();
            if (!key) continue;
            freq[key] = (freq[key] || 0) + 1;
          }
          return Object.entries(freq)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([k]) => k);
        };

        const rMap = {};
        for (const r of ratingsRows) {
          const name = r.Doctorname || r.name || r.doctorname || '';
          if (!name) continue;
          const avg = Number.isFinite(r.avg_rating) ? Number(r.avg_rating) : 0;
          rMap[name] = {
            avg_rating: Math.max(0, Math.min(100, Math.round(avg))),
            topTags: buildTopTags(r),
          };
        }
        if (!mounted) return;
        setRatingsMap(rMap);

        const vMap = {};
        for (const d of baseList) {
          const n = d?.name || '';
          if (!n) continue;
          const sc = shortCodeFromName(n);
          vMap[n] = {
            shortCode: sc,
            shortPath: `/doctor-reviews/${sc}`,
            path: `/doctor-reviews/${sc}`,
          };
        }

        if (!mounted) return;
        setViewsMap(vMap);
      } catch {
        if (!mounted) return;
        setDirectory([]);
        setRatingsMap({});
        setViewsMap({});
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const collegeOptions = useMemo(() => {
    const s = new Set();
    directory.forEach((d) => {
      if (d && d.college) s.add(String(d.college).trim());
    });
    return ['الكل', ...Array.from(s)];
  }, [directory]);

  const departmentOptions = useMemo(() => {
    const s = new Set();
    directory.forEach((d) => {
      const inCollege =
        !selectedCollege ||
        selectedCollege === 'الكل' ||
        (d && String(d.college || '').trim() === selectedCollege);
      if (inCollege && d && d.Department) s.add(String(d.Department).trim());
    });
    return ['الكل', ...Array.from(s)];
  }, [directory, selectedCollege]);

  const mergedList = useMemo(() => {
    return directory.map((d, idx) => {
      const name = d?.name || '';
      const rateInfo = ratingsMap[name] || { avg_rating: 0, topTags: [] };
      const viewInfo = viewsMap[name] || {
        path: `/doctor-reviews/${shortCodeFromName(name)}`,
      };
      return {
        id: `${name}-${idx}`,
        name,
        college: d?.college || '',
        department: d?.Department || '',
        specialty: d?.specialty || '',
        rating: rateInfo.avg_rating || 0,
        tags: Array.isArray(rateInfo.topTags) ? rateInfo.topTags : [],
        profilePath: viewInfo.path,
      };
    });
  }, [directory, ratingsMap, viewsMap]);


  const filtered = useMemo(() => {
    const normalize = (s) => arNorm(s || '');
    const queryNorm = normalize(selectedNameFilter || '');
    const queryTokens = queryNorm.split(' ').filter(Boolean);

    return mergedList.filter((row) => {
      if (selectedCollege && selectedCollege !== 'الكل' && row.college !== selectedCollege) return false;
      if (selectedDepartment && selectedDepartment !== 'الكل' && row.department !== selectedDepartment) return false;

      if (queryTokens.length > 0) {
        const rowNorm = normalize(row.name);
        const ok = queryTokens.every((t) => rowNorm.includes(t));
        if (!ok) return false;
      }

      return true;
    });
  }, [mergedList, selectedCollege, selectedDepartment, selectedNameFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (selectedRatingFilter === 'الأكثر تقييما') arr.sort((a, b) => b.rating - a.rating);
    else if (selectedRatingFilter === 'الأقل تقييما') arr.sort((a, b) => a.rating - b.rating);
    return arr;
  }, [filtered, selectedRatingFilter]);

  const totalPages = Math.ceil(sorted.length / itemsPerPage) || 1;
  const currentLecturers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sorted.slice(start, start + itemsPerPage);
  }, [sorted, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCollege, selectedDepartment, selectedRatingFilter, selectedNameFilter]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.filter-dropdown')) setShowRatingDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSearchSuggestions = async (query) => {
    const q = (query || '').trim();
    if (!q) {
      setSearchSuggestions([]);
      setShowSearchSuggestions(false);
      return;
    }
    try {
      const results = await fetchJSON(
        `${DIRECTORY_SEARCH_URL}?q=${encodeURIComponent(q)}`
      );
      const suggestions = (Array.isArray(results) ? results : []).slice(0, 8);
      setSearchSuggestions(suggestions);
      setShowSearchSuggestions(suggestions.length > 0);
    } catch {
      setSearchSuggestions([]);
      setShowSearchSuggestions(false);
    }
  };

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchQuery(v);
    setSelectedNameFilter(v);
    getSearchSuggestions(v);
  };

  const handleSuggestionSelect = (sug) => {
    const pickedName = sug?.name || '';
    setSelectedNameFilter(pickedName);
    setSearchQuery(pickedName);
    setShowSearchSuggestions(false);
    setCurrentPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const typed = (searchQuery || '').trim();
      if (!typed) {
        setSelectedNameFilter('');
        setShowSearchSuggestions(false);
        setCurrentPage(1);
        return;
      }
      if (searchSuggestions.length > 0) {
        handleSuggestionSelect(searchSuggestions[0]);
      } else {
        setSelectedNameFilter(typed);
        setShowSearchSuggestions(false);
        setCurrentPage(1);
      }
    }
  };

  const handleLecturerClick = (lecturer) => {
    const href =
      lecturer?.profilePath ||
      `/doctor-reviews/${shortCodeFromName(lecturer?.name)}`;
    navigate(href);
  };

  const handleAddLecturer = async (lecturerData) => {
    try {
      const res = await fetch(DIRECTORY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lecturerData),
      });

      if (!res.ok) throw new Error('Failed to add lecturer');

      const updated = await fetchJSON(DIRECTORY_URL);
      const baseList = Array.isArray(updated) ? updated : [];
      setDirectory(baseList);
    } catch (err) {
      console.error(err);
      alert('تعذّر إضافة المحاضر، حاول مرة أخرى');
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 80) return 'rgba(162, 233, 189, 1)';
    if (rating >= 60) return 'rgba(242, 194, 102, 1)';
    if (rating >= 30) return 'rgba(250, 134, 134, 1)';
    return 'rgba(255, 0, 0, 1)';
  };

  const renderProgressCircle = (rating) => {
    const color = getRatingColor(rating);
    const radius = 35;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (rating / 100) * circumference;

    return (
      <div className="relative w-[80px] h-[80px] md:w-[120px] md:h-[120px]">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={radius} stroke="#374151" strokeWidth="6" fill="transparent" />
          <circle
            cx="60"
            cy="60"
            r={radius}
            stroke={color}
            strokeWidth="6"
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
          <circle
            cx="60"
            cy="25"
            r="3"
            fill={color}
            className="transition-all duration-1000 ease-out"
            style={{ transform: `rotate(${(rating / 100) * 360}deg)`, transformOrigin: '60px 60px' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white text-sm md:text-2xl font-bold">{rating}%</span>
        </div>
      </div>
    );
  };

  const handleSelectEvaluationType = (type) => {
    if (type === 'lecturers') setShowEvaluationModal(false);
    else if (type === 'courses') navigate('/course-evaluation');
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <EvaluationModal
        isOpen={showEvaluationModal}
        onClose={() => setShowEvaluationModal(false)}
        onSelectEvaluationType={handleSelectEvaluationType}
      />

      <AddLecturerModal
        isOpen={showAddLecturerModal}
        onClose={() => setShowAddLecturerModal(false)}
        onSubmit={handleAddLecturer}
      />

      <div
        className="min-h-screen text-white bg-transparent relative z-10 pt-12 md:pt-12 px-4 md:px-8 md:mr-[80px]"
        dir="ltr"
      >
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-2xl md:text-4xl font-bold mb-2 md:mb-4">تقييم المحاضرين</h1>
          <p className="text-gray-300 text-sm md:text-lg px-4">اطلع على تقييمات المحاضرين وتجارب الطلاب</p>
        </div>

        <div className="mb-6 md:mb-8">
          <div className="relative mb-4 md:mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="ابحث عن المحاضر..."
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => getSearchSuggestions(searchQuery)}
                onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 100)}
                dir="rtl"
                className="w-full bg-[#0f1e2e] border border-[#28e3da40] rounded-xl px-12 py-3 md:py-4 text-white placeholder-gray-400 focus:outline-none focus:border-[#28e3da] transition-colors text-sm md:text-base text-right"
              />
              <div
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-[999]"
              >
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {showSearchSuggestions && searchSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a2a3a] border border-[#28e3da40] rounded-lg shadow-lg z-[999] max-h-60 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {searchSuggestions.map((sug, idx) => (
                  <button
                    key={`${sug.name}-${idx}`}
                    onMouseDown={() => handleSuggestionSelect(sug)}
                    className="w-full text-right px-4 py-3 text-sm hover:bg-[#2a3a4a] transition-colors text-white border-b border-[#28e3da20] last:border-b-0"
                  >
                    <div className="flex flex-col items-end text-right w-full">
                      <span className="font-medium text-white w-full text-right">{sug.name}</span>
                      <span className="text-gray-400 text-xs w-full text-right">
                        {sug.college || '—'}
                        {sug.Department ? ` — ${sug.Department}` : ''}
                        {sug.specialty ? ` — ${sug.specialty}` : ''}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedNameFilter && (
            <div className="mb-4 flex justify-end">
              <span
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs"
                style={{
                  background:
                    'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
                  border: '1px solid rgba(40, 227, 218, 0.45)',
                }}
              >
                <span className="text-white font-medium">{selectedNameFilter}</span>
                <span className="text-white/80">:الاسم</span>
                <button
                  onClick={() => {
                    setSelectedNameFilter('');
                    setSearchQuery('');
                    setCurrentPage(1);
                  }}
                  className="ml-1 text-white/70 hover:text:white"
                  aria-label="إزالة فلتر الاسم"
                  title="إزالة فلتر الاسم"
                >
                  ×
                </button>
              </span>
            </div>
          )}

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end" dir="ltr">
            <div className="relative filter-dropdown">
              <select
                value={selectedCollege || 'الكل'}
                onChange={(e) => setSelectedCollege(e.target.value)}
                className="bg-transparent border border-gray-400 text-white rounded-xl px-4 py-2 hover:bg-[#1a2a3a] focus:outline-none"
                dir="rtl"
              >
                {collegeOptions.map((c) => (
                  <option className="bg-[#0f1e2e]" key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative filter-dropdown">
              <select
                value={selectedDepartment || 'الكل'}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="bg-transparent border border-gray-400 text-white rounded-xl px-4 py-2 hover:bg-[#1a2a3a] focus:outline-none"
                dir="rtl"
              >
                {departmentOptions.map((d) => (
                  <option className="bg-[#0f1e2e]" key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative filter-dropdown">
              <button
                onClick={() => setShowRatingDropdown(!showRatingDropdown)}
                className={`transition-all duration-200 flex items-center gap-3 ${
                  selectedRatingFilter !== 'التقييم'
                    ? 'border border-[#EEEEEE] text-[#EEEEEE]'
                    : 'border border-gray-400 text-white hover:bg-[#1a2a3a]'
                }`}
                style={{
                  width: 'auto',
                  minWidth: selectedRatingFilter !== 'التقييم' ? '128px' : 'auto',
                  height: '44px',
                  borderRadius: '12px',
                  padding: selectedRatingFilter !== 'التقييم' ? '13px 32px' : '12px 16px',
                  background:
                    selectedRatingFilter !== 'التقييم'
                      ? 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)'
                      : 'transparent',
                  fontFamily: 'Alexandria, sans-serif',
                  fontWeight: '800',
                  fontSize: '14px',
                  lineHeight: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                <span>{selectedRatingFilter}</span>
                {selectedRatingFilter === 'التقييم' && (
                  <div className="w-4 h-4 border border-white rounded-full flex items-center justify-center">
                    <div className="w-0 h-0 border-l-[3px] border-r-[3px] border-t-[3px] border-transparent border-t-white"></div>
                  </div>
                )}
              </button>

              {showRatingDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full bg-[#0f1e2e] border border-[#28e3da40] rounded-lg shadow-lg z-10">
                  {['التقييم', 'الأكثر تقييما', 'الأقل تقييما'].map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        setSelectedRatingFilter(option);
                        setShowRatingDropdown(false);
                      }}
                      className={`w-full text-right px-3 py-2 text-xs md:text-sm hover:bg-[#28e3da20] transition-colors text-right ${
                        selectedRatingFilter === option ? 'text[#28e3da]' : 'text-white'
                      }`}
                      style={{ textAlign: 'right' }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="text-center mb-4">
          <p className="text-gray-300 text-sm md:text-base">
            عرض {currentLecturers.length} من {sorted.length} محاضر
            {(selectedCollege && selectedCollege !== 'الكل') ||
            (selectedDepartment && selectedDepartment !== 'الكل') ||
            selectedNameFilter ? (
              <span className="text-[#28e3da]"> (مفلتر)</span>
            ) : null}
            {selectedRatingFilter !== 'التقييم' && <span className="text-[#28e3da]"> (مرتب)</span>}
          </p>
        </div>

        <div className="space-y-3 md:space-y-6 mb-8">
          {currentLecturers.length > 0 ? (
            currentLecturers.map((lecturer) => (
              <div
                key={lecturer.id}
                className="bg-transparent border border-white rounded-xl md:rounded-2xl p-3 md:p-6 flex items-center gap-3 md:gap-6 cursor-pointer hover:bg-[#1a2a3a] transition-colors"
                onClick={() => handleLecturerClick(lecturer)}
                title="افتح صفحة الدكتور"
              >
                <div className="flex-shrink-0">{renderProgressCircle(lecturer.rating)}</div>

                <div className="flex-1 text-right space-y-1.5 md:space-y-3">
                  <div className="space-y-1 md:space-y-2">
                    <p className="text-white text-sm md:text-lg font-medium">
                      <span className="text-gray-400 ml-1 md:ml-2 text-xs md:text-base">اسم المحاضر:</span>
                      {lecturer.name}
                    </p>
                    <p className="text-gray-300 text-xs md:text-base">
                      <span className="text-gray-400 ml-1 md:ml-2 text-xs md:text-base">الكلية:</span>
                      {lecturer.college || '—'}
                    </p>
                    <p className="text-gray-300 text-xs md:text-base">
                      <span className="text-gray-400 ml-1 md:ml-2 text-xs md:text-base">القسم:</span>
                      {lecturer.department || '—'}
                    </p>
                    <p className="text-gray-300 text-xs md:text-base">
                      <span className="text-gray-400 ml-1 md:ml-2 text-xs md:text-base">التخصص:</span>
                      {lecturer.specialty || '—'}
                    </p>
                  </div>

                  {Array.isArray(lecturer.tags) && lecturer.tags.length > 0 ? (
                    <div className="flex gap-1 md:gap-2 justify-end flex-wrap">
                      {lecturer.tags.slice(0, 5).map((tag, i) => (
                        <span
                          key={`${tag}-${i}`}
                          className="flex items-center gap-2 px-3 py-1 rounded-3xl text-white text-xs font-light"
                          style={{
                            background:
                              'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
                            border: '1px solid #2A9E8D',
                            borderRadius: '24px',
                            fontFamily: 'Alexandria, sans-serif',
                            fontSize: '10px',
                            lineHeight: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            textAlign: 'right',
                          }}
                        >
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{
                              background: '#5FCC9C',
                              boxShadow: '0px 3px 20px rgba(162, 233, 189, 0.6)',
                              borderRadius: '10px',
                            }}
                          ></div>
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg md:text-xl mb-2">لا توجد نتائج</div>
              <p className="text-gray-500 text-sm md:text-base">جرّب تغيير الفلاتر أو ابحث عن محاضر آخر</p>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 md:gap-3 overflow-x-auto px-4 md:px-0">
            {currentPage > 1 && (
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="flex items:center gap-1 md:gap-2 px-2 md:px-3 py-2 text-xs md:text-sm transition-colors text-gray-300 hover:text-white whitespace-nowrap"
              >
                <span className="text-base md:text-lg">&lt;</span>
                <span className="hidden sm:inline">السابق</span>
              </button>
            )}

            {(() => {
              const pages = [];
              const maxVisible =
                typeof window !== 'undefined' && window.innerWidth < 768 ? 5 : 7;
              if (totalPages <= maxVisible) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                let end = Math.min(totalPages, start + maxVisible - 1);
                if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
                if (start > 1) {
                  pages.push(1);
                  if (start > 2) pages.push('...');
                }
                for (let i = start; i <= end; i++) pages.push(i);
                if (end < totalPages) {
                  if (end < totalPages - 1) pages.push('...');
                  pages.push(totalPages);
                }
              }

              return pages.map((p, idx) => (
                <button
                  key={`${p}-${idx}`}
                  onClick={() => typeof p === 'number' && setCurrentPage(p)}
                  className={`px-2 md:px-3 py-2 text-xs md:text-sm transition-colors whitespace-nowrap ${
                    typeof p === 'number' && currentPage === p
                      ? 'text-[#28e3da] font-medium'
                      : typeof p === 'number'
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-500 cursor-default'
                  }`}
                  disabled={typeof p !== 'number'}
                >
                  {p}
                </button>
              ));
            })()}

            {currentPage < totalPages && (
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 text-xs md:text-sm transition-colors text-gray-300 hover:text-white whitespace-nowrap"
              >
                <span className="hidden sm:inline">التالي</span>
                <span className="text-base md:text-lg">&gt;</span>
              </button>
            )}
          </div>
        )}

        {isAuthenticated ? (
          <div className="mt-8 mb-8">
            <div
              style={{
                width: '100%',
                height: 'auto',
                padding: '20px 17.5px',
                background:
                  'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                boxShadow: 'rgba(0, 0, 0, 0.25) 0px 4px 4px',
                borderRadius: 42,
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 4,
                display: 'flex',
                cursor: 'pointer',
                transition: '0.3s',
                transform: 'scale(1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              onClick={() => {
                setShowAddLecturerModal(true);
              }}
            >
              <div
                style={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 8,
                  display: 'flex',
                }}
              >
                <div
                  style={{
                    color: 'var(--ألوان-القائمة-text-color, white)',
                    fontSize: 12,
                    fontFamily: 'Alexandria',
                    fontWeight: '500',
                    wordWrap: 'break-word',
                    transform: 'scale(1)',
                  }}
                >
                  اطلب إضافة محاضر
                </div>
                <svg
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.50684 12.0008C7.50684 11.8019 7.58585 11.6111 7.72651 11.4705C7.86716 11.3298 8.05792 11.2508 8.25684 11.2508H11.7498V7.75781C11.7498 7.5589 11.8289 7.36813 11.9695 7.22748C12.1102 7.08683 12.3009 7.00781 12.4998 7.00781C12.6987 7.00781 12.8895 7.08683 13.0302 7.22748C13.1708 7.36813 13.2498 7.5589 13.2498 7.75781V11.2508H16.7428C16.9417 11.2508 17.1325 11.3298 17.2732 11.4705C17.4138 11.6111 17.4928 11.8019 17.4928 12.0008C17.4928 12.1997 17.4138 12.3905 17.2732 12.5311C17.1325 12.6718 16.9417 12.7508 16.7428 12.7508H13.2498V16.2438C13.2498 16.4427 13.0302 16.6335 13.0302 16.7741C12.8895 16.9148 12.6987 16.9938 12.4998 16.9938C12.3009 16.9938 12.1102 16.9148 11.9695 16.7741C11.8289 16.6335 11.7498 16.4427 11.7498 16.2438V12.7508H8.25684C8.05792 12.7508 7.86716 12.6718 7.72651 12.5311C7.58585 12.3905 7.50684 12.1997 7.50684 12.0008Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.81667 3.76856C10.9291 3.4235 14.0702 3.4235 17.1827 3.76856C19.0097 3.97256 20.4847 5.41156 20.6987 7.24856C21.0687 10.4056 21.0687 13.5946 20.6987 16.7516C20.4837 18.5886 19.0087 20.0266 17.1827 20.2316C14.0702 20.5766 10.9291 20.5766 7.81667 20.2316C5.98967 20.0266 4.51467 18.5886 4.30067 16.7516C3.93233 13.5946 3.93233 10.4055 4.30067 7.24856C4.51467 5.41156 5.99067 3.97256 7.81667 3.76856ZM17.0167 5.25856C14.0145 4.92579 10.9848 4.92579 7.98267 5.25856C7.42691 5.32021 6.90817 5.56743 6.51025 5.96028C6.11233 6.35312 5.85846 6.86864 5.78967 7.42356C5.434 10.4645 5.434 13.5366 5.78967 16.5776C5.85867 17.1323 6.11263 17.6476 6.51053 18.0402C6.90844 18.4329 7.42706 18.6799 7.98267 18.7416C10.9597 19.0736 14.0397 19.0736 17.0167 18.7416C17.5721 18.6797 18.0905 18.4326 18.4882 18.0399C18.8859 17.6473 19.1397 17.1321 19.2087 16.5776C19.5643 13.5366 19.5643 10.4645 19.2087 7.42356C19.1395 6.86915 18.8856 6.35421 18.4879 5.96178C18.0902 5.56936 17.5719 5.32234 17.0167 5.26056"
                    fill="white"
                  />
                </svg>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-8 mb-8 text-center">
            <button
              onClick={openHeaderLoginModal}
              className="flex items-center justify-center gap-2 mx-auto transition-all duration-200 border border-[#EEEEEE] rounded-xl"
              style={{
                width: '310px',
                height: '44px',
                borderRadius: '12px',
                fontFamily: 'Alexandria, sans-serif',
                fontSize: '16px',
                fontWeight: '700',
                lineHeight: '100%',
                color: '#EEEEEE',
                background: 'transparent',
              }}
              aria-label="سجّل دخولك عشان التقييم"
              title="سجّل دخولك عشان التقييم"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M15 3h4a2 2 0 012 2v4" />
                <path d="M10 14L21 3" />
                <path d="M21 10v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9" />
              </svg>
              <span>سجّل دخولك عشان تضيف محاضر</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default LecturerEvaluationPage;
